import { faCoin, faPlus } from "@fortawesome/pro-regular-svg-icons";

import { ChangeEvent, FunctionComponent, useCallback, useMemo, useState } from "react";

import { useTranslation } from "react-i18next";

import { zodResolver } from "@hookform/resolvers/zod";

import { useForm } from "react-hook-form";

import trashIcon from "@/assets/icons/trashIcon.svg?url";

import { Icon, Modal } from "@/components";
import { InputField } from "@/components/InputField/InputField";
import { ButtonSize } from "@/components/Button/types";

import { Button } from "@/components/Button/Button";

import { AiApplyType, CustomColumnTypes, PriorityFilterStatus, UpdateType } from "@/enum/list.enum";
import { ERROR_TYPE, getAllErrors, renderErrorMessages } from "@/utils";

import { ColumnConfig } from "@/pages/Private/pages/List/schema/columnConfig";

import { useUpdateListColumnsMutation } from "@/pages/Private/redux/list/list.api";

import { listSelector } from "@/pages/Private/redux/list/list.slice";
import { useAppSelector } from "@/redux/hooks";
import { Dropdown, AutoCompleteItem } from "@/components/Dropdown/Dropdown";

import { CustomColumn, CustomColumnSchema, List } from "../../../schema/list";
import { getCurrentPrices } from "../../../utils/currentPrices";
import { CustomColumnApplyFilters } from "../customColumnApplyFilters";

export interface DepartmentModalProps {
	setShowConfirm: (value: boolean) => void;
	open: boolean;
	listId: number;
	list?: List;
	setOpen: (value: boolean) => void;
	columnId?: number;
	column?: ColumnConfig;
	customColumnName?: string;
	filters?: CustomColumnApplyFilters;
	selectedProspectIds?: number[];
	priorityFilterStatus?: PriorityFilterStatus[];
}

export const DepartmentModal: FunctionComponent<DepartmentModalProps> = ({
	setShowConfirm,
	listId,
	list,
	open,
	setOpen,
	columnId,
	column,
	customColumnName,
	filters,
	selectedProspectIds,
	priorityFilterStatus,
}) => {
	const [randomValues, setRandomValues] = useState<string[]>(
		column?.config ? (JSON.parse(column.config as string) as string[]) : []
	);

	const { meta } = useAppSelector(listSelector);

	const [randomValue, setRandomValue] = useState<string>();
	const [applyTo, setApplyTo] = useState<AiApplyType>(AiApplyType.NONE);
	const { t } = useTranslation();
	const ts = useCallback((key: string) => t(`aiColumn.${key}`), [t]);
	const { inbox } = useAppSelector(listSelector);
	const [update, { isLoading, error }] = useUpdateListColumnsMutation();

	const {
		handleSubmit,
		getValues,
		setValue,
		formState: { errors },
	} = useForm<CustomColumn>({
		defaultValues: {
			customColumnType: CustomColumnTypes.DEPARTMENT_SIZE,
			...column,
			customColumnName: customColumnName || column?.name,
		},
		resolver: zodResolver(CustomColumnSchema),
	});

	const prospectIds =
		applyTo === AiApplyType.FIRST_10
			? inbox.slice(0, 10).map((p) => p.id)
			: applyTo === AiApplyType.CURRENTLY_SELECTED
				? selectedProspectIds
				: undefined;

	const onSubmit = async (values: CustomColumn) => {
		try {
			values.value = JSON.stringify(randomValues);

			await update({
				columnId,
				updateType: UpdateType.CUSTOM_COLUMN,
				...values,
				id: listId,
				aiApplyType: applyTo,
				prospectIds: prospectIds,
				filters: applyTo === AiApplyType.CURRENTLY_FILTERED ? filters : undefined,
				priorityFilterStatus: priorityFilterStatus ?? undefined,
			}).unwrap();

			setOpen(false);
		} catch (err) {
			console.error(err);
		}
	};

	const formErrors = Object.values(errors).map((error) => error?.message) as ERROR_TYPE[];

	const applyToButtonText = useMemo(() => {
		return applyTo === AiApplyType.NONE
			? ts(`button.${AiApplyType.NONE}`)
			: applyTo === AiApplyType.FIRST_10
				? ts(`button.${AiApplyType.FIRST_10}`)
				: applyTo === AiApplyType.CURRENTLY_FILTERED
					? ts(`button.${AiApplyType.CURRENTLY_FILTERED}`)
					: ts(`button.${AiApplyType.ALL}`);
	}, [applyTo, ts]);

	const currentCreditCost = useMemo(() => {
		return getCurrentPrices({
			applyTo,
			meta,
			singlePrice: 1,
			selectedProspectIds,
			list,
		});
	}, [applyTo, getValues("online"), meta.itemCount, selectedProspectIds?.length]);

	return (
		<Modal
			disableSave={!randomValues.length}
			handleClose={() => {
				setShowConfirm(true);
			}}
			handleSave={handleSubmit(onSubmit)}
			isLoading={isLoading}
			isOpened={open}
			size="md"
			submitButtonText={applyToButtonText}
			title="Create department size column"
		>
			<div className="text-ssm font-medium mb-4">
				<InputField
					error={!!errors.customColumnName}
					handleChange={function (event: ChangeEvent<HTMLInputElement>): void {
						setValue("customColumnName", event.target.value, {
							shouldValidate: true,
							shouldDirty: true,
						});
					}}
					label="Column Name"
					name={"name"}
					placeholder="Enter column name"
					value={getValues("customColumnName")}
				/>
			</div>

			<div className="flex flex-col">
				<div className="flex flex-row items-start space-y-4 -mb-2">
					<InputField
						className="!mb-0 grow mr-4"
						error={!!errors.value}
						handleChange={function (event: ChangeEvent<HTMLInputElement>): void {
							setRandomValue(event.target.value);
						}}
						name={"randomValue"}
						placeholder="Enter job title... (e.g. Software Engineer, Business Development)"
						value={randomValue ?? ""}
						onKeyDown={(e) => {
							if (e.key === "Enter" && randomValues.length < 10) {
								if (randomValue) {
									setRandomValues([randomValue, ...(randomValues as string[])]);
								}

								setRandomValue("");
							}
						}}
					/>
					<div className="w-[140px] !mt-0 ml-4 flex items-start">
						<Button
							className="!mt-0"
							disabled={!randomValue || randomValues.length >= 10}
							image={<Icon className="mr-2" icon={faPlus} />}
							size={ButtonSize.L}
							title="Add value"
							onClick={() => {
								setRandomValues([...(randomValues as string[]), randomValue ?? ""]);
								setRandomValue("");
							}}
						/>
					</div>
				</div>
				<div className="flex flex-col text-xs text-text-meta">Maximum 10 job titles</div>
			</div>
			<div>
				{randomValues.length > 0 && (
					<div className="text-ssm font-medium mt-4 mb-2">
						List of job titles to define department:
					</div>
				)}
				{(randomValues as string[]).map((value, index) => (
					<div
						key={index}
						className="flex items-center justify-between h-[54px] px-2.5 py-4.5 rounded-xl border border-gray-200 mb-4"
					>
						<div>{value}</div>
						<button
							className="cursor-pointer"
							onClick={() => {
								const values = [...(randomValues as string[])];

								values.splice(index, 1);
								setRandomValues(values);
							}}
						>
							<img alt="trash" src={trashIcon} />
						</button>
					</div>
				))}
			</div>

			<div className="flex flex-row mt-5 border-t pt-5 border-gray-200">
				<Dropdown
					data={[
						{
							title: ts(AiApplyType.NONE),
							id: AiApplyType.NONE,
						},
						{
							title: ts(AiApplyType.FIRST_10),
							id: AiApplyType.FIRST_10,
						},
						{
							title: ts(AiApplyType.CURRENTLY_FILTERED),
							id: AiApplyType.CURRENTLY_FILTERED,
						},
						{
							title: ts(AiApplyType.CURRENTLY_SELECTED),
							id: AiApplyType.CURRENTLY_SELECTED,
						},
						{
							title: ts(AiApplyType.ALL),
							id: AiApplyType.ALL,
						},
						{
							title: ts(AiApplyType.ALL_INCLUDING_ASSIGNED),
							id: AiApplyType.ALL_INCLUDING_ASSIGNED,
						},
					]}
					floating={false}
					handleSelect={function (value?: AutoCompleteItem) {
						if (value?.id) {
							setApplyTo(value.id as AiApplyType);
						}
					}}
					label="Apply to ..."
					value={applyTo ? { title: ts(applyTo), id: applyTo } : undefined}
				/>
			</div>

			<>
				<div className="flex flex-row mt-5 border-t pt-5 border-gray-200 text-gray-700 items-center text-sm">
					<span className="mr-1">1x</span>
					<Icon className="w-[16px] h-[16px] text-gray-700 mr-1" icon={faCoin} />
					<span className="">per row</span>
				</div>
				{applyTo && currentCreditCost > 0 && (
					<div className="flex flex-row pt-5 text-gray-700 items-center text-sm">
						<span className="mr-1">{currentCreditCost}</span>
						<Icon className="w-[16px] h-[16px] text-gray-700 mr-1" icon={faCoin} />
						<span className="">total</span>
					</div>
				)}
			</>

			{getAllErrors(error, formErrors).length ? (
				<div className="mt-4 mb-0">{renderErrorMessages(getAllErrors(error, formErrors))}</div>
			) : null}
		</Modal>
	);
};
